/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import {
  BULK_EXPORT_UI_STATUS,
  BULK_PUBLISH_C6_STATUS,
  BULK_PUBLISH_POST_C6_UI_STATUS,
  BULK_UI_STATUS,
  EXPORT_STATUS,
  PUBLISH_STATUS
} from '../constants/bulkActionStatusConstants';

/**
 * Convert a status string specifically for bulk publish operations
 * @param {string} str - The status string to convert
 * @returns {string} The formatted status string for UI display
 */
export const toPublishTitleCase = str => {
  if (!str) return BULK_UI_STATUS.PROCESSING;

  const statusMapping = {
    [PUBLISH_STATUS.FAILURE.toLowerCase()]: BULK_UI_STATUS.ERROR,
    [PUBLISH_STATUS.FAILED.toLowerCase()]: BULK_UI_STATUS.ERROR,
    [PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase()]: BULK_UI_STATUS.ERROR,
    [PUBLISH_STATUS.PENDING.toLowerCase()]: BULK_UI_STATUS.PROCESSING,
    [PUBLISH_STATUS.QUEUED.toLowerCase()]: BULK_UI_STATUS.PROCESSING,
    [PUBLISH_STATUS.IN_PROGRESS.toLowerCase()]: BULK_UI_STATUS.PROCESSING,
    [PUBLISH_STATUS.DELIVERED.toLowerCase()]: BULK_UI_STATUS.COMPLETED
  };

  const lowerStr = str.toLowerCase();
  if (statusMapping[lowerStr]) {
    return statusMapping[lowerStr];
  }

  return BULK_UI_STATUS.PROCESSING;
};

/**
 * Convert a status string specifically for bulk export operations
 * @param {string} str - The status string to convert
 * @returns {string} The formatted status string for UI display
 */
export const toExportTitleCase = str => {
  if (!str) return BULK_UI_STATUS.PROCESSING;

  const statusMapping = {
    [EXPORT_STATUS.ERROR.toLowerCase()]: BULK_UI_STATUS.ERROR,
    [EXPORT_STATUS.FAILED.toLowerCase()]: BULK_UI_STATUS.ERROR,
    [EXPORT_STATUS.QUEUED.toLowerCase()]: BULK_UI_STATUS.PROCESSING,
    [EXPORT_STATUS.IN_PROGRESS.toLowerCase()]: BULK_UI_STATUS.PROCESSING,
    [EXPORT_STATUS.COMPLETE.toLowerCase()]: BULK_UI_STATUS.COMPLETED
  };

  const lowerStr = str.toLowerCase();
  // Check if it's a known status value that needs special handling
  if (statusMapping[lowerStr]) {
    return statusMapping[lowerStr];
  }

  // For unknown status values, return a default
  return BULK_UI_STATUS.PROCESSING;
};

export const toPublishProjectListTitleCase = str => {
  if (!str) return '';

  const statusMapping = {
    [PUBLISH_STATUS.FAILURE.toLowerCase()]: BULK_UI_STATUS.FAILED,
    [PUBLISH_STATUS.FAILED.toLowerCase()]: BULK_UI_STATUS.FAILED,
    [PUBLISH_STATUS.FAILED_TO_PUBLISH.toLowerCase()]: BULK_UI_STATUS.FAILED,
    [PUBLISH_STATUS.PENDING.toLowerCase()]: BULK_UI_STATUS.QUEUED,
    [PUBLISH_STATUS.QUEUED.toLowerCase()]: BULK_UI_STATUS.QUEUED,
    [PUBLISH_STATUS.IN_PROGRESS.toLowerCase()]: BULK_UI_STATUS.IN_PROGRESS,
    [PUBLISH_STATUS.DELIVERED.toLowerCase()]: BULK_UI_STATUS.COMPLETED
  };

  const lowerStr = str.toLowerCase();
  if (statusMapping[lowerStr]) {
    return statusMapping[lowerStr];
  }

  return BULK_UI_STATUS.QUEUED;
};

export const toExportProjectListTitleCase = str => {
  if (!str) return '';

  const statusMapping = {
    [EXPORT_STATUS.ERROR.toLowerCase()]: BULK_EXPORT_UI_STATUS.ERROR,
    [EXPORT_STATUS.FAILED.toLowerCase()]: BULK_EXPORT_UI_STATUS.ERROR,
    [EXPORT_STATUS.QUEUED.toLowerCase()]: BULK_EXPORT_UI_STATUS.QUEUED,
    [EXPORT_STATUS.IN_PROGRESS.toLowerCase()]: BULK_EXPORT_UI_STATUS.PROCESSING,
    [EXPORT_STATUS.COMPLETE.toLowerCase()]: BULK_EXPORT_UI_STATUS.COMPLETED
  };

  const lowerStr = str.toLowerCase();
  if (statusMapping[lowerStr]) {
    return statusMapping[lowerStr];
  }

  return BULK_UI_STATUS.QUEUED;
};

export const determinePostC6StatusFromRequest = postC6Status => {
  if (!postC6Status) return '';

  const statusMapping = {
    [BULK_PUBLISH_C6_STATUS.FAILED.toLowerCase()]:
      BULK_PUBLISH_POST_C6_UI_STATUS.FAILED,
    [BULK_PUBLISH_C6_STATUS.DELIVERED.toLowerCase()]:
      BULK_PUBLISH_POST_C6_UI_STATUS.DELIVERED,
    [BULK_PUBLISH_C6_STATUS.NOT_STARTED.toLowerCase()]:
      BULK_PUBLISH_POST_C6_UI_STATUS.NOT_STARTED
  };

  const lowerStr = postC6Status.toLowerCase();
  if (statusMapping[lowerStr]) {
    return statusMapping[lowerStr];
  }

  return '';
};

export const getBulkProjectStatusTrackingColumns = () => [
  {
    header: 'S.No',
    field: 'index',
    className: 'project-status-tracking__index_column',
    style: {},
    align: 'center'
  },
  {
    header: 'Project Name',
    field: 'title',
    className: 'project-status-tracking__project_name_column',
    style: {},
    align: 'center'
  },
  {
    header: 'Publish started',
    field: 'startTime',
    className: 'project-status-tracking__date_column',
    style: {},
    align: 'center',
    isSortable: true,
    sortField: 'startTime'
  },
  {
    header: 'Publish ended',
    field: 'endTime',
    className: 'project-status-tracking__date_column',
    style: {},
    align: 'center',
    isSortable: true,
    sortField: 'endTime'
  },
  {
    header: 'Status',
    field: 'status',
    className: 'project-status-tracking__status_column',
    style: {},
    isSortable: true,
    sortField: 'status',
    align: 'center'
  },
  {
    header: 'Post C6 Status',
    field: 'postC6Status',
    className: 'project-status-tracking__status_column',
    style: {},
    align: 'center'
  },
  {
    header: 'Error details',
    field: 'errorDetails',
    className: 'project-status-tracking__error_column',
    style: {},
    align: 'center'
  }
];
